import {
  faFileExport,
  faFilter,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import SearchBox from 'components/common/SearchBox';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useState, ChangeEvent, useEffect } from 'react';
import { Card, Col, Row, Toast, ToastContainer } from 'react-bootstrap';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider';
import { Course } from '../my_learning/components/DataDefinitions';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { useNavigate } from "react-router-dom";
import { Dropdown } from 'react-bootstrap';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import Badge from 'components/base/Badge';




const CoursesTable = () => {
  return (
    <div className="border-top">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
      <AdvanceTableFooter pagination className="py-4" />
    </div>
  );
};



const AdminCourses = () => {
  const auth = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);

  // State to control Toast messages
  const [show, setShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');


  const breadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'Dashboard',
      url: '/dashboard/home'
    },
    {
      label: 'Courses (Admin)',
      active: true
    }
  ];

  const ActionDropdownItems = ({ course, onSponsor, onActivate, onDelete }: {
    course: Course;
    onSponsor: (course_id: string | number, current_value: boolean) => void;
    onActivate: (course_id: string | number, current_value: boolean) => void;

    onDelete: (course_id: string | number) => void;
  }) => {
    return (
      <>
        <Dropdown.Item eventKey="1" onClick={() => onSponsor(course.course_id, course.sponsored)} >
          {course.sponsored == true ? 'Remove from trending' : 'Set as trending'}
        </Dropdown.Item>

        <Dropdown.Item eventKey="1" onClick={() => onActivate(course.course_id, course.active)} >
          {course.active == true ? 'Draft' : 'Published'}
        </Dropdown.Item>

        <Dropdown.Divider />

        <Dropdown.Item eventKey="2" onClick={() => onDelete(course.course_id)} className="text-danger">
          Delete course
        </Dropdown.Item>
      </>
    );
  };

  const coursesTableColumns: ColumnDef<Course>[] = [
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row: { original } }) => {
        return (
          <div>
            <Link to={`/admin/courses/${original.course_id}/update`} className="fs-8 fw-bold">
              {original.title}
            </Link>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '50%' }, className: 'ps-0' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },
    {
      accessorKey: 'topic',
      header: 'Topic',
      cell: ({ row: { original } }) => {
        return (
          <div>
            {original.topic}
          </div>
        );
      },
      meta: {
        headerProps: {
          style: { width: '15%' }, className: 'ps-0'
        },
        cellProps: { className: 'white-space-nowrap fw-semi-bold' }
      }
    },
    {
      accessorKey: 'sponsored',
      header: 'Trending',
      cell: ({ row: { original } }) => {
        return (
          original.sponsored ? (<Badge variant="phoenix" bg='success'>trending</Badge>) : "-"
        );
      },
      meta: {
        headerProps: { style: { width: '8%' }, className: 'ps-0' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },
    {
      accessorKey: 'active',
      header: 'Published',
      cell: ({ row: { original } }) => {
        return (
          <Badge variant="phoenix" bg={original.active ? 'success' : 'warning'}>
            {original.active ? 'On' : 'Off'}
          </Badge>
        );
      },
      meta: {
        headerProps: { style: { width: '5%' }, className: 'ps-0' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },
    {
      accessorKey: 'inserted_at',
      header: 'Create date',
      cell: ({ row: { original } }) => original.inserted_at,
      meta: {
        headerProps: {
          style: { width: '10%' },
          className: 'ps-4 pe-5'
        },
        cellProps: {
          className: 'white-space-nowrap ps-4 text-700'
        }
      }
    },
    {
      id: 'courseDropdown',
      cell: ({ row: { original } }) => {
        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <ActionDropdownItems course={original} onSponsor={() => sponsorCourse(original.course_id, original.sponsored)}
                onActivate={() => activateCourse(original.course_id, original.sponsored)}

                onDelete={deleteCourse} />

            </RevealDropdown>
          </RevealDropdownTrigger>
        );
      },
      meta: {
        cellProps: {
          className: 'text-end pe-0 ps-4'
        }
      }
    }
  ];

  const fetchData = async () => {
    const response = await apiClient.get(
      '/v1/courses/admin',
      {
        headers: {
          'accept': 'application/json',

        },
        withCredentials: true,
      }
    );
    setCourses(response.data.courses);
  };

  useEffect(() => {
    fetchData();
  }, []);


  const table = useAdvanceTable({
    data: courses,
    columns: coursesTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true,
    initialState: {
      columnVisibility: {
        status: false,
        designation: false
      }
    }
  });
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const navigate = useNavigate();
  const handleClick = () => {
    navigate('/admin/courses/add'); // Navigate to the AddCourse page
  };

  const deleteCourse = async (course_id: string | number) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      const response = await apiClient.delete(
        `/v1/courses/${course_id}`,
        {
          headers: {
            'accept': 'application/json',

          }
        }
      );
      setToastMessage(response.data['message'])
      setShow(true);
      fetchData();
    }
  };


  const sponsorCourse = async (course_id: string | number, current_value: boolean) => {

    const response = await apiClient.put(
      `/v1/courses/${course_id}/sponsor/${current_value}`, {},
      {
        headers: {
          'accept': 'application/json',

        }
      }
    );
    setToastMessage(response.data['message'])
    setShow(true);
    fetchData();

  };

  const activateCourse = async (course_id: string | number, current_value: boolean) => {
    const response = await apiClient.put(
      `/v1/courses/${course_id}/activate/${current_value}`, {},
      {
        headers: {
          'accept': 'application/json',

        }
      }
    );
    setToastMessage(response.data['message'])
    setShow(true);
    fetchData();

  };

  return (
    <>
      <PageBreadcrumb items={breadcrumbItems} />

      <ToastContainer className="p-3" position='top-center'>
        <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Card className='mt-3  mb-3 border border-light rounded-5' >
        <Card.Body>
          <AdvanceTableProvider {...table}>

            <Row className="g-3 justify-content-between mb-4">
              <Col xs="auto">
                <Button
                  variant="primary"
                  className="me-4"
                  startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
                  onClick={handleClick}
                >
                  Create a course
                </Button>

              </Col>
              <Col xs="auto">
                <div className="d-flex">
                  <SearchBox
                    placeholder="Search by name"
                    className="me-2"
                    onChange={handleSearchInputChange}
                  />
                  <DatePicker defaultValue="Mar 1, 2022" />
                  <Button
                    variant="phoenix-secondary"
                    className="px-3 ms-2"
                    onClick={() => setOpenFilterModal(true)}
                  >
                    <FontAwesomeIcon
                      icon={faFilter}
                      transform="down-3"
                      className="text-primary"
                    />
                  </Button>
                </div>
                <LeadsFilterModal
                  show={openFilterModal}
                  handleClose={() => setOpenFilterModal(false)}
                />
              </Col>
            </Row>
            <CoursesTable />
          </AdvanceTableProvider>
        </Card.Body>
      </Card>
    </>
  );
};

export default AdminCourses;